import { useCallback, useEffect, useRef, useState } from "react";
import { FencyContentStatus, SignedDocumentDto, ViewDocumentAsOwnerDto } from "../../autogen/bff-api";
import { useFency } from "./useFency";
import { parseFencyContentStatus } from "./parseFencyContentStatus";
import { getPusher } from "../pusher";

export const useFencyDocumentSync = (
  document: ViewDocumentAsOwnerDto | SignedDocumentDto,
  refetchDocuments?: () => void | Promise<void>
) => {
  const { triggerSync } = useFency();
  const [contentStatus, setContentStatus] = useState<FencyContentStatus | null>(
    document.fencyContentStatus ?? null
  );
  const [syncError, setSyncError] = useState(false);

  const handleFencyMemoryUpdated = useCallback(
    (data: unknown) => {
      const status = parseFencyContentStatus(data);
      if (status) {
        setContentStatus(status);
        void refetchDocuments?.();
      }
    },
    [refetchDocuments]
  );

  // One sync trigger per document id: the refetch after a trigger hands the effect a NEW document
  // object that is typically still SYNCHRONIZING, so re-triggering on object identity loops
  // trigger→refetch→trigger until the webhook lands (and forever when it never does). The Pusher
  // event below delivers the status flip; re-triggering adds nothing.
  const triggeredIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (document.fencyContentStatus === FencyContentStatus.Synchronized) {
      return;
    }

    if (triggeredIdRef.current !== document.id) {
      triggeredIdRef.current = document.id;
      const runSync = async () => {
        try {
          setSyncError(false);
          await triggerSync(document);
          void refetchDocuments?.();
        } catch {
          setSyncError(true);
        }
      };
      void runSync();
    }

    const channel = getPusher().subscribe(document.id);
    channel.bind("fency-memory-updated", handleFencyMemoryUpdated);

    return () => {
      channel.unbind("fency-memory-updated", handleFencyMemoryUpdated);
    };
  }, [document, document.fencyContentStatus, triggerSync, handleFencyMemoryUpdated, refetchDocuments]);

  useEffect(() => {
    if (document.fencyContentStatus === FencyContentStatus.Synchronized) {
      setContentStatus(FencyContentStatus.Synchronized);
    }
  }, [document.fencyContentStatus]);

  return {
    isReady: contentStatus === FencyContentStatus.Synchronized,
    syncError,
    contentStatus,
  };
};
